// The answer control for "make the amount" questions (21 Oct 2026, Year 3):
// the coins and notes (or base-ten blocks) to choose from, and a tray. A tap
// on a piece puts one in the tray; a tap on a piece in the tray takes it out.
// No total is shown: adding the pieces up is the skill. Wording approved by
// the owner on 21 Oct 2026.
import {
  isMoneyBuildPiece,
  MAX_TRAY,
  pieceName,
  splitTray,
  type BuildPiece,
  type BuildQuestion,
} from '../../platform/content/buildAnswer';
import { CountPictureView } from '../../platform/content/practice-count';
import { MoneyPictureView } from '../../platform/content/practice-money';

type Props = {
  question: BuildQuestion;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
};

const BLOCK_COUNTS: Record<string, string> = {
  thousand: 'thousands',
  hundred: 'hundreds',
  ten: 'tens',
  one: 'ones',
};

// One piece, drawn the way the pictures draw it.
function PiecePicture({ piece }: { piece: BuildPiece }) {
  return (
    <span aria-hidden="true" className="pointer-events-none block w-full">
      {isMoneyBuildPiece(piece) ? (
        <MoneyPictureView
          money={{ type: 'money', items: [{ value: piece, count: 1 }] }}
        />
      ) : (
        <CountPictureView
          count={{
            type: 'base_ten',
            thousands: 0,
            hundreds: 0,
            tens: 0,
            ones: 0,
            [BLOCK_COUNTS[piece] ?? 'ones']: 1,
          }}
        />
      )}
    </span>
  );
}

export function MakeAmountAnswer({
  question,
  value,
  disabled,
  onChange,
}: Props) {
  const tray = splitTray(question, value);
  const money = question.kind === 'money';
  return (
    <div className="grid gap-3">
      <p className="font-semibold">Tap to add</p>
      <div className="flex flex-wrap gap-2">
        {question.pieces.map((piece) => (
          <button
            key={piece}
            type="button"
            aria-label={`Add ${pieceName(piece)}`}
            disabled={disabled || tray.length >= MAX_TRAY}
            className="flex w-20 items-center justify-center rounded-xl border-2 border-line bg-white p-1.5 hover:border-line-strong"
            onClick={() => {
              onChange([...tray, piece].join('\n'));
            }}
          >
            <PiecePicture piece={piece} />
          </button>
        ))}
      </div>
      <p className="font-semibold">{money ? 'Your amount' : 'Your blocks'}</p>
      <div
        role="group"
        aria-label={money ? 'Your amount' : 'Your blocks'}
        className="flex min-h-16 flex-wrap gap-2 rounded-xl border-2 border-dashed border-forest-600 bg-sage p-2"
      >
        {tray.map((piece, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Take out ${pieceName(piece)}`}
            disabled={disabled}
            className="flex w-16 items-center justify-center rounded-lg bg-white p-1"
            onClick={() => {
              onChange(tray.filter((_, at) => at !== index).join('\n'));
            }}
          >
            <PiecePicture piece={piece} />
          </button>
        ))}
      </div>
      <p className="text-base text-muted">
        {tray.length === 0 ? 'Nothing here yet.' : 'Tap one to take it out.'}
      </p>
      {tray.length > 0 && (
        <button
          type="button"
          disabled={disabled}
          className="dm-link-quiet w-fit"
          onClick={() => {
            onChange('');
          }}
        >
          Start again
        </button>
      )}
    </div>
  );
}
